import { inject } from '@angular/core';
import {
  CanMatchFn,
  PartialMatchRouteSnapshot,
  Route,
  UrlSegment,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { mapFn, throwErrorExp } from '@core/actions/util';
import { selectEntityMap } from '@core/state/entities/entity.selectors';
import { RequestStatus } from '@core/signal-store-features/request-state.feature';
import { entityKey } from '@core/state/entities/entity-name-map';
import {
  selectLoadedProjectId,
  selectLoadingStatusForProject,
  selectProjectIsLoading,
  selectReviewerScreeningStatsStatus,
} from '@core/state/ui/project/project-ui.selectors';
import { LoadProjectRequest } from '@core/requests';
import { selectStageReviewerAnnotationStatsStatusMap } from '@core/state/ui/stage/stage-ui.selectors';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { v4 as uuid } from 'uuid';
import { combineLatest, filter, firstValueFrom, map, take } from 'rxjs';
import {
  ProjectMemberStatus,
  selectAreMembershipStatsLoadedOrLoadingForProject,
  selectAreProjectStatsLoadedOrLoadingForProject,
  selectAreReviewerStatsLoadedOrLoadingForProject,
  selectMemberStatusForProject,
  selectProjectById,
} from '@core/state/entities/project/project.selectors';
import { ProjectLoadingStatus } from '@core/state/ui/project/project-ui.reducer';
import { selectProjectDetailsForProject } from '@core/state/entities/project-details/project-details.selectors';

export const projectCanMatchGuard: CanMatchFn = async (
  route: Route,
  segments: UrlSegment[],
  _currentSnapshot: PartialMatchRouteSnapshot
) => {
  const store = inject(Store);
  const loadProjectRequest = inject(LoadProjectRequest);
  const projectId = segments[0].path;

  const $projectDetailsForProject = store.selectSignal(
    selectProjectDetailsForProject(projectId)
  );
  const $currentLoadedProjectId = store.selectSignal(selectLoadedProjectId);
  const $areReviewerStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreReviewerStatsLoadedOrLoadingForProject(projectId)
  );
  const $areMembershipStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreMembershipStatsLoadedOrLoadingForProject(projectId)
  );
  const $areProjectStatsLoadedOrLoadingForProject = store.selectSignal(
    selectAreProjectStatsLoadedOrLoadingForProject(projectId)
  );

  const $memberStatus = store.selectSignal(
    selectMemberStatusForProject(projectId)
  );

  if (
    $memberStatus() === ProjectMemberStatus.Idle ||
    $memberStatus() === ProjectMemberStatus.Error
  ) {
    loadProjectRequest.dispatchRequest.loadProject({
      projectId,
    });
  }
  if ($memberStatus() === ProjectMemberStatus.Loading) {
    await firstValueFrom(
      store
        .select(selectMemberStatusForProject(projectId))
        .pipe(
          filter((memberStatus) => memberStatus !== ProjectMemberStatus.Loading)
        )
    );
  }

  const onIsMember = async () => {
    // Dispatch stat loading actions if needed
    if (!$areReviewerStatsLoadedOrLoadingForProject()) {
      store.dispatch(
        projectDetailActions.retrieveProjectReviewerStats({
          projectId,
          correlationId: uuid(),
        })
      );
    }
    if (
      !$areMembershipStatsLoadedOrLoadingForProject() ||
      !$areProjectStatsLoadedOrLoadingForProject()
    ) {
      store.dispatch(
        projectDetailActions.retrieveFullProjectStats({
          projectId,
          correlationId: uuid(),
        })
      );
    }

    if (
      $currentLoadedProjectId() !== projectId ||
      !$projectDetailsForProject()
    ) {
      // Dispatch request to load project
      loadProjectRequest.dispatchRequest.loadProject({
        projectId,
      });

      // Wait for either success (data appears) or failure (error status)
      // This handles the race condition where multiple requests might be exhausted
      const canAccess = await firstValueFrom(
        combineLatest([
          store.select(selectProjectDetailsForProject(projectId)),
          store.select(selectLoadingStatusForProject(projectId)),
        ]).pipe(
          // Wait until loading is complete (either success or error)
          filter(
            ([details, loadingStatus]) =>
              details !== null ||
              loadingStatus === ProjectLoadingStatus.Error ||
              loadingStatus === ProjectLoadingStatus.NotFound
          ),
          // Map to access decision
          map(([details, loadingStatus]) => {
            // Deny access if load failed
            if (
              loadingStatus === ProjectLoadingStatus.Error ||
              loadingStatus === ProjectLoadingStatus.NotFound
            ) {
              return false;
            }
            // Allow access if details loaded successfully
            return details !== null;
          }),
          take(1)
        )
      );

      return canAccess;
    } else {
      return true;
    }
  };

  const memberStatus = $memberStatus();

  return memberStatus === ProjectMemberStatus.Member
    ? await onIsMember()
    : memberStatus === ProjectMemberStatus.NotMember ||
        memberStatus === ProjectMemberStatus.NotFound ||
        memberStatus === ProjectMemberStatus.Error
      ? false
      : throwErrorExp('Unexpected member status');
};
